import { useLatest } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { getFramework, getOperateType, getPreviewModeWrapperId, useFramework, useIsBasicOperate } from '@/framework';
import { chartInnerViewClassName } from '@/layers/chart-layer/view/chart-layer';
import { checkElementIsLayerViewNode, getLayerIdAndLayerTypeByElement, getLayerViewNode } from '@/utils/layer-kit';
import { BaseLayerViewType, IChartLayer, IExportPlugin, LayerType, PluginType } from '@/version-types';

const useChartLayerViewNodes = (frameworkIdentity: string) => {
    const isBasicOperateRef = useLatest(useIsBasicOperate());
    const [chartLayerIds, setChartLayerIds] = useState<string[]>([]);
    const [els, setEls] = useState<HTMLElement[]>([]);

    useEffect(() => {
        const framework = getFramework(frameworkIdentity);
        const listenerId = framework.events.onLayerChange(() => {
            if (isBasicOperateRef.current) return;

            const chartLayerIds = framework.stage
                .getActiveStageLayers()
                .filter((layer) => layer.layerType === LayerType.CHART)
                .map((layer) => layer.id);
            setChartLayerIds(chartLayerIds);
        });
        return () => {
            framework.events.off(listenerId);
            setChartLayerIds([]);
        };
    }, [frameworkIdentity]);

    useEffect(() => {
        const els = chartLayerIds
            .map((layerId) => getLayerViewNode(frameworkIdentity, layerId))
            .filter(Boolean) as HTMLElement[];
        setEls(els);
    }, [chartLayerIds.sort().join(',')]);

    return els;
};

const useActiveChartLayer = () => {
    // --- framework hooks ---
    const framework = useFramework();

    // --- internal states ---
    const [targetLayer, setTargetLayer] = useState<IChartLayer | null>(null);

    // --- internal refs ---
    const targetLayerIdRef = useLatest(targetLayer?.id ?? null);

    useEffect(() => {
        const activeUpdateListenerID = framework.events.onActiveLayerDataUpdate((activeLayer, pickLayer) => {
            const getTargetTableLayer = (): IChartLayer | null => {
                if (!activeLayer) return null;

                let maybeTargetLayer = activeLayer;
                if (activeLayer.layerType === LayerType.GROUP_LAYER && pickLayer) {
                    maybeTargetLayer = pickLayer;
                }

                if (maybeTargetLayer.layerType !== LayerType.CHART) return null;
                return maybeTargetLayer as IChartLayer;
            };

            setTargetLayer(getTargetTableLayer());
        });

        const baseOperateListenerID = framework.events.onBaseOperate((layerId) => {
            if (layerId !== targetLayerIdRef.current) return;

            const layer = framework.getLayer(layerId);
            setTargetLayer(layer as IChartLayer);
        });

        return () => {
            framework.events.offEvent(activeUpdateListenerID);
            framework.events.offEvent(baseOperateListenerID);
        };
    }, []);

    return targetLayer;
};

/** 图表鼠标hover的交互逻辑 */
const ChartHoverWidget: React.FC = React.memo(() => {
    // --- framework states ---
    const framework = useFramework();

    const isBasicOperate = useIsBasicOperate();
    const isBasicOperateRef = useLatest(isBasicOperate);
    const frameworkIdentityRef = useLatest(framework.identity);
    const activeChartLayerId = useActiveChartLayer()?.id ?? null;
    const activeChartLayerIdRef = useLatest(activeChartLayerId);
    const chartLayerViewNodes = useChartLayerViewNodes(framework.identity);
    const [moveOperateNode, setMoveOperateNode] = useState<HTMLElement | null>(null);

    const getWrapperDomId = () => {
        const previewId = getPreviewModeWrapperId(frameworkIdentityRef.current || '');
        const isPreviewMode = document.querySelector(`#${previewId}`) !== null;
        const wrapperDomId = isPreviewMode ? previewId : frameworkIdentityRef.current;
        return wrapperDomId;
    };

    // 尽量拿到dom，拿不到就算了，小bug不影响
    useEffect(() => {
        setTimeout(() => {
            setMoveOperateNode(document.querySelector<HTMLElement>('[data-operate-type="move"]'));
        }, 300);
    }, [activeChartLayerId]);

    // mousemove事件通知
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isBasicOperateRef.current) return;

            const el = e.target;
            const wrapperDomId = getWrapperDomId() || '';
            const isChartLayerViewNode =
                el instanceof HTMLElement && checkElementIsLayerViewNode(wrapperDomId, el, LayerType.CHART);
            const isChartMoveDom = !!activeChartLayerIdRef.current && getOperateType(el) === BaseLayerViewType.MOVE;
            if (!isChartLayerViewNode && !isChartMoveDom) return;

            const layerId = isChartMoveDom
                ? activeChartLayerIdRef.current!
                : getLayerIdAndLayerTypeByElement(wrapperDomId, el as HTMLElement)!.layerId;
            if (!framework.getLayer(layerId)) return;

            const chartEl = getLayerViewNode(wrapperDomId, layerId)?.querySelector(`.${chartInnerViewClassName}`);
            if (!chartEl) return;
            const eventTarget = chartEl.querySelector('canvas') ?? chartEl.querySelector('svg');
            if (!eventTarget) return;
            const stopPropagation = (e: Event) => e.stopPropagation();
            // 防止外部聚焦框出现抖动
            chartEl.addEventListener('mousemove', stopPropagation);
            // 将MouseEvent事件传给dom
            eventTarget.dispatchEvent(new MouseEvent('mousemove', e));
            chartEl.removeEventListener('mousemove', stopPropagation);
        };
        document.addEventListener('mousemove', handleMouseMove);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    // mouseleave事件通知, 退出时取消鼠标hover效果
    useEffect(() => {
        const getHandleMouseLeave = (layerId?: string) => {
            return (e: MouseEvent) => {
                if (isBasicOperateRef.current) return;

                const el = e.target;
                const wrapperDomId = getWrapperDomId() || '';
                if (!(el instanceof HTMLElement)) return;

                if (!layerId) {
                    layerId = el.getAttribute('data-layer-id')!;
                }
                if (!framework.getLayer(layerId)) return;

                const chartEl = getLayerViewNode(wrapperDomId, layerId)?.querySelector(`.${chartInnerViewClassName}`);
                if (!chartEl) return;
                const eventTarget = chartEl.querySelector('canvas') ?? chartEl.querySelector('svg');
                if (!eventTarget) return;
                const stopPropagation = (e: Event) => e.stopPropagation();
                // 防止外部聚焦框出现抖动
                chartEl.addEventListener('mousemove', stopPropagation);
                // 移动到空白区域, 以取消hover效果
                eventTarget.dispatchEvent(
                    new MouseEvent('mousemove', {
                        clientX: 0,
                        clientY: 0,
                        screenX: 0,
                        screenY: 0,
                        movementX: 0,
                        movementY: 0,
                        cancelable: true,
                        bubbles: true,
                    }),
                );
                chartEl.removeEventListener('mousemove', stopPropagation);
            };
        };
        const viewNodeHandleMouseLeave = getHandleMouseLeave();
        const operateNodeMouseLeave = (e: MouseEvent) => {
            if (!activeChartLayerIdRef.current) return;
            getHandleMouseLeave(activeChartLayerIdRef.current)(e);
        };

        // 非激活态时, 用的是chartLayerViewNodes
        chartLayerViewNodes.forEach((el) => {
            el.addEventListener('mouseleave', viewNodeHandleMouseLeave);
        });

        // 激活态时, 用的就是moveOperateNode了
        moveOperateNode?.addEventListener('mouseleave', operateNodeMouseLeave);

        return () => {
            chartLayerViewNodes.forEach((el) => {
                el.removeEventListener('mouseleave', viewNodeHandleMouseLeave);
            });
            moveOperateNode?.removeEventListener('mouseleave', operateNodeMouseLeave);
        };
    }, [moveOperateNode, chartLayerViewNodes]);

    useEffect(() => {
        if (!activeChartLayerId) return;

        const hanldeClick = (e: MouseEvent) => {
            if (getOperateType(e.target) !== BaseLayerViewType.MOVE) return;

            const wrapperDomId = getWrapperDomId() || '';
            const chartEl = getLayerViewNode(wrapperDomId, activeChartLayerId)?.querySelector(
                `.${chartInnerViewClassName}`,
            );
            if (!chartEl) return;
            const eventTarget = chartEl.querySelector('canvas') ?? chartEl.querySelector('svg');
            if (!eventTarget) return;
            const stopPropagation = (e: Event) => e.stopPropagation();
            // 防止外部聚焦框出现抖动
            chartEl.addEventListener(e.type, stopPropagation);
            // 将MouseEvent事件传给dom
            eventTarget.dispatchEvent(new MouseEvent(e.type, e));
            chartEl.removeEventListener(e.type, stopPropagation);
        };

        document.addEventListener('click', hanldeClick, { capture: true });
        document.addEventListener('mousedown', hanldeClick, { capture: true });
        document.addEventListener('mouseup', hanldeClick, { capture: true });
        return () => {
            document.removeEventListener('click', hanldeClick, { capture: true });
            document.removeEventListener('mousedown', hanldeClick, { capture: true });
            document.removeEventListener('mouseup', hanldeClick, { capture: true });
        };
    }, [activeChartLayerId]);

    return null;
});

ChartHoverWidget.displayName = 'ChartHoverWidget';

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 3,
    name: 'chart-hover',
    render: () => <ChartHoverWidget />,
};

export default exportPlugin;
